import { useEffect, useRef } from 'react';
import {
    FinancingApi,
    LoanStatus,
    GetLoanApiResponse,
    SubmitApplicationDecision,
} from '@servicetitan/financing-api-client';
import { useDependencies } from '@servicetitan/react-ioc';
import { useQuery, useQueryClient } from '@tanstack/react-query';

const DEFAULT_POLL_INTERVAL = 5000; // 5 seconds
const DEFAULT_MAX_POLL_TIME = 120000; // 2 minutes

export interface UseLoanStatusPollingOptions {
    /** Loan ID to poll for status updates */
    loanId: string | null;
    /** Custom poll interval in milliseconds (default: 5000) */
    pollInterval?: number;
    /** Custom max poll time in milliseconds (default: 120000) */
    maxPollTime?: number;
    /** Custom timeout callback */
    onTimeout?: (loanId: string) => void;
    /** Custom decision change callback - called when loan decision changes */
    onDecisionChange?: (
        decision: SubmitApplicationDecision,
        loanData: GetLoanApiResponse,
        loanId: string,
    ) => void;
    /** Whether polling is enabled (default: true when loanId exists) */
    enabled?: boolean;
}

export interface UseLoanStatusPollingReturn {
    /** Current loan data from polling */
    loanData: GetLoanApiResponse | undefined;
    /** Whether polling is currently active */
    isPolling: boolean;
    /** Stop polling manually */
    stopPolling: () => void;
    /** Start polling manually */
    startPolling: () => void;
    /** Reset polling state */
    reset: () => void;
}

export const useLoanStatusPolling = (
    options: UseLoanStatusPollingOptions,
): UseLoanStatusPollingReturn => {
    const {
        loanId,
        pollInterval = DEFAULT_POLL_INTERVAL,
        maxPollTime = DEFAULT_MAX_POLL_TIME,
        onTimeout,
        onDecisionChange,
        enabled = true,
    } = options;

    const [financingApi] = useDependencies(FinancingApi);
    const queryClient = useQueryClient();
    const timeoutRef = useRef<NodeJS.Timeout>();

    // Set up timeout when polling starts
    useEffect(() => {
        if (loanId && enabled) {
            timeoutRef.current = setTimeout(() => {
                // Cancel the polling query
                queryClient.cancelQueries({ queryKey: ['loanStatus', loanId] });

                if (onTimeout) {
                    onTimeout(loanId);
                }
                // Note: Decision data and workflow stage management should be handled in the component
            }, maxPollTime);

            return () => {
                if (timeoutRef.current) {
                    clearTimeout(timeoutRef.current);
                }
            };
        }
    }, [loanId, enabled, queryClient, onTimeout, maxPollTime]);

    // Query for polling loan status
    const { data: loanData, refetch } = useQuery<GetLoanApiResponse>({
        queryKey: ['loanStatus', loanId],
        queryFn: async () => {
            const response = await financingApi.loansGetUpdatedApplication(loanId!);
            return response.data;
        },
        enabled: !!loanId && enabled,
        refetchInterval: query => {
            const status = query.state.data?.status;
            if (
                status === LoanStatus.Declined ||
                status === LoanStatus.Rejected ||
                status === LoanStatus.Approved ||
                status === LoanStatus.Prequalified ||
                status === LoanStatus.Error
            ) {
                return false;
            }
            return pollInterval;
        },
        refetchOnWindowFocus: false,
        retry: false,
    });

    // Effect to handle loan status changes
    useEffect(() => {
        if (loanData?.status && loanId) {
            if (
                timeoutRef.current &&
                (loanData.status === LoanStatus.Declined ||
                    loanData.status === LoanStatus.Rejected ||
                    loanData.status === LoanStatus.Approved ||
                    loanData.status === LoanStatus.Prequalified ||
                    loanData.status === LoanStatus.Error)
            ) {
                clearTimeout(timeoutRef.current);
            }

            let decision: SubmitApplicationDecision;

            // Map loan status to decision
            switch (loanData.status) {
                case LoanStatus.Declined:
                case LoanStatus.Rejected:
                    decision = SubmitApplicationDecision.Declined;
                    break;
                case LoanStatus.Approved:
                case LoanStatus.Prequalified:
                    decision = SubmitApplicationDecision.Approved;
                    break;
                case LoanStatus.Review:
                case LoanStatus.AboutToSubmit:
                    decision = SubmitApplicationDecision.Review;
                    break;
                default:
                    decision = SubmitApplicationDecision.Error;
                    break;
            }

            // Always call the callback if provided
            if (onDecisionChange) {
                onDecisionChange(decision, loanData, loanId);
            }
        }
    }, [loanData, loanId, onDecisionChange]);

    const stopPolling = () => {
        queryClient.cancelQueries({ queryKey: ['loanStatus', loanId] });
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
    };

    const startPolling = () => {
        if (loanId) {
            refetch();
        }
    };

    const reset = () => {
        stopPolling();
    };

    return {
        loanData,
        isPolling: !!loanId && enabled,
        stopPolling,
        startPolling,
        reset,
    };
};
